import React, { useCallback, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { Autocomplete, TextField } from '@mui/material';

import rateGroupService, { RateGroup } from '~/services/cruises/RateGroupService';

interface Props {
  label: string;
  rateGroupIds: Array<string>;
  vendorId: string;
  onChange: (rateGroups: Array<RateGroup>) => void;
  disabled?: boolean;
}

const ITEMS_PER_PAGE = 80;

function InputRateGroupSelect({ label, rateGroupIds, vendorId, onChange, disabled }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const { data: rateGroups = [], isLoading } = useQuery({
    queryKey: ['cruise', 'rate-group-select', vendorId],
    queryFn: async () => {
      try {
        const res = await rateGroupService.list({
          take: ITEMS_PER_PAGE,
          vendorId,
        });
        return res.result;
      } catch (e) {
        // ignore abort errors
        if (e.name === 'AbortError') {
          return;
        }
        enqueueSnackbar('There was an error while fetching rate groups', {
          variant: 'error',
        });
        throw e;
      }
    },
    enabled: !!vendorId,
  });

  const handleChange = useCallback(
    (_: unknown, value: Array<RateGroup>) => {
      onChange(value);
    },
    [onChange],
  );

  const selectedRateGroups = useMemo(() => {
    return rateGroups.filter((rg) => rateGroupIds?.includes(rg.id));
  }, [rateGroups, rateGroupIds]);

  return (
    <Autocomplete
      multiple
      options={rateGroups}
      value={selectedRateGroups}
      loading={isLoading}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.name}
      onChange={handleChange}
      disabled={disabled}
      renderInput={(params) => <TextField {...params} fullWidth label={label} placeholder="Please Enter Rate Groups" />}
    />
  );
}

export default InputRateGroupSelect;
